import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { TableProps } from 'antd/lib/table';
import Pagination from 'antd/es/pagination';
import { LOG_ACTION } from './LogModal';
import { DbFolderPermissionActionLog } from '~src/types/db';
import { useFolderPermissionActionLog } from '~src/hooks/api-hooks/useSecurityGroupActionLog';
import { formatDate } from '~src/utils/formatDate';

interface FolderPermissionsLogProps {
  state: boolean;
  before: number;
}

const TAKE_LIMIT = 12;

export const FolderPermissionsLog: React.FC<FolderPermissionsLogProps> = ({
  state,
  before,
}) => {
  const intl = useIntl();
  const [skipLimit, setSkipLimit] = useState(0);
  const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>();

  const folderPermissionActionLogQuery = useFolderPermissionActionLog(
    { before, take: TAKE_LIMIT, skip: skipLimit * TAKE_LIMIT },
    {
      enabled: state,
    }
  );

  useEffect(() => {
    if (state) {
      folderPermissionActionLogQuery.refetch();
    }
  }, [state]);

  useEffect(() => {
    if (!totalNumberOfItems) {
      setTotalNumberOfItems(folderPermissionActionLogQuery.data?.totalCount);
    }
  }, [folderPermissionActionLogQuery.data]);

  const FolderPermissionActionLogSource = useMemo(
    () =>
      (folderPermissionActionLogQuery.data?.logs || []).map((element) => ({
        ...element,
        key: element.action_id,
      })),
    [folderPermissionActionLogQuery.data]
  );

  const folderPermissionLogColums: TableProps<DbFolderPermissionActionLog>['columns'] = [
    {
      title: intl.formatMessage({ id: 'date-and-time' }),
      dataIndex: 'action_at',
      key: 'date',
      render: (action_at) => formatDate(action_at, 'long'),
      width: '20%',
    },
    {
      title: intl.formatMessage({ id: 'security-groups' }),
      dataIndex: 'security_group_name',
      key: 'security-groups',
      width: '25%',
    },
    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: 'folder_path',
      key: 'path',
      width: '30%',
    },
    {
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action_type',
      key: 'action',
      render: (action) =>
        Object.keys(LOG_ACTION).filter((x) => x === action).length >= 1
          ? intl.formatMessage({ id: LOG_ACTION[action] })
          : action,
      width: '10%',
    },
    {
      title: intl.formatMessage({ id: 'by' }),
      dataIndex: 'create_by_name',
      key: 'by',
      width: '15%',
    },
  ];

  return (
    <div>
      <div style={{ position: 'relative', height: 'min(65vh, 600px)' }}>
        <Table<DbFolderPermissionActionLog>
          showSorterTooltip={false}
          columns={folderPermissionLogColums}
          dataSource={FolderPermissionActionLogSource}
          pagination={false}
          loading={folderPermissionActionLogQuery.isLoading}
          scroll={{ y: 'min(50vh, 450px)', scrollToFirstRowOnChange: true }}
        />
      </div>
      {totalNumberOfItems && totalNumberOfItems > 0 && (
        <Pagination
          style={{ position: 'absolute', bottom: 40, right: 0, margin: '20px' }}
          total={totalNumberOfItems}
          pageSize={12}
          showSizeChanger={false}
          onChange={(page) => {
            setSkipLimit(page - 1);
          }}
        />
      )}
    </div>
  );
};
